<template>
  <div class="home-padding container-xxl">
    <div class="header-background">
      <h2 class="header-title">Lesson Library</h2>
      <p class="lesson-library-description">
        Foster innovation and problem-solving through hands-on, virtual
        project-based learning.
      </p>
    </div>

    <div class="category-filters">
      <button
        v-for="category in categories"
        :key="category"
        class="category-button"
        :class="{ active: selectedCategory === category }"
        @click="selectCategory(category)"
      >
        {{ category }}
      </button>
    </div>

    <div class="lesson-cards">
      <div v-if="!isMobile" class="maker-activities-container">
        <div class="cards-container">
          <div v-if="isLessonLoading" class="loading-overlay">
            <div class="loader-spinner"></div>
          </div>
          <div
            v-if="lessons.length === 0 && !isLessonLoading"
            class="empty-state fade-in"
          >
            <div class="empty-icon">
              <svg
                width="80"
                height="80"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 6.25278V19.2528M12 6.25278C10.8321 5.47686 9.24649 5 7.5 5C5.75351 5 4.16789 5.47686 3 6.25278V19.2528C4.16789 18.4769 5.75351 18 7.5 18C9.24649 18 10.8321 18.4769 12 19.2528M12 6.25278C13.1679 5.47686 14.7535 5 16.5 5C18.2465 5 19.8321 5.47686 21 6.25278V19.2528C19.8321 18.4769 18.2465 18 16.5 18C14.7535 18 13.1679 18.4769 12 19.2528"
                  stroke="#0d3b66"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <h3 class="empty-title">No lessons found</h3>
            <p class="empty-message">No lessons match the selected category.</p>
            <button @click="clearFilters" class="reset-button">
              Show all lessons
            </button>
          </div>
          <div v-else-if="!isLessonLoading" class="lesson-grid">
            <LessonLibraryCard
              v-for="(lesson, index) in lessons"
              :key="lesson.id"
              :lesson="lesson"
              :isLogged="isLogged"
              class="fade-in"
              :style="{ animationDelay: `${index * 0.05}s` }"
              @card-clicked="viewLesson(lesson)"
              @update-liked-status="updateLessonLikedStatus"
            />
          </div>
        </div>
      </div>

      <!-- Mobile Carousel for Lessons -->
      <div v-else class="mobile-carousel-container">
        <swiper
          :modules="[SwiperPagination, SwiperNavigation, SwiperAutoplay]"
          :slides-per-view="1.2"
          :space-between="0"
          :centered-slides="true"
          :loop="lessons.length > 3"
          :autoplay="false"
          :grab-cursor="true"
          class="lesson-swiper"
        >
          <swiper-slide
            v-for="lesson in lessons"
            :key="lesson.id"
            class="lesson-slide"
          >
            <LessonLibraryCard
              :lesson="lesson"
              :isLogged="isLogged"
              @card-clicked="viewLesson(lesson)"
              @update-liked-status="updateLessonLikedStatus"
            />
          </swiper-slide>
        </swiper>
      </div>

      <div class="load-more-container" v-if="lessons.length > 0">
        <ActionButton
          variant="primary"
          @click="loadMoreLessons"
          class="load-more-button"
          :disabled="isLessonLoading"
        >
          <span>View More Lessons</span>
        </ActionButton>
      </div>
    </div>

    <div class="teacher-testimonials">
      <div class="header-background">
        <h3 class="header-title">Hear from our teachers</h3>
      </div>
      <div class="video-container" :class="{ 'video-playing': isVideoPlaying }">
        <div
          v-if="!isVideoPlaying"
          class="video-thumbnail"
          role="button"
          tabindex="0"
          @click="playVideo"
          @keydown.enter="playVideo"
          @keydown.space.prevent="playVideo"
        >
          <div class="thumbnail-overlay"></div>
          <img
            src="https://img.youtube.com/vi/wB2adKKB6Zo/maxresdefault.jpg"
            alt="Eddy Teacher Testimonial Video Thumbnail"
            class="thumbnail-img"
          />
          <div class="play-button">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8 5.14V19.14L19 12.14L8 5.14Z" fill="white" />
            </svg>
          </div>
          <div class="video-title">
            <div class="eddy-logo-small">Eddy</div>
            <p class="eddy-tagline">The Educator's Buddy</p>
          </div>
        </div>
        <div v-else class="youtube-video-wrapper">
          <iframe
            width="100%"
            height="100%"
            :src="`https://www.youtube.com/embed/wB2adKKB6Zo?autoplay=1&rel=0`"
            title="Eddy Teacher Testimonial"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          ></iframe>
          <button
            class="close-video-btn"
            @click="closeVideo"
            aria-label="Close video"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div class="header-background">
      <h2 class="header-title">Maker Activities</h2>
      <p class="lesson-library-description">
        Spark curiosity with our hands-on Maker Activities! These
        standard-aligned activities boost engagement, foster creativity, and
        deepen learning-empowering students to explore, create, and innovate
      </p>
    </div>
    <div class="lesson-cards">
      <div v-if="!isMobile" class="maker-activities-container">
        <div class="cards-container">
          <div v-if="isLoading" class="loading-overlay">
            <div class="loader-spinner"></div>
          </div>

          <div
            v-if="makerActivities.length === 0 && !isLoading"
            class="empty-state fade-in"
          >
            <div class="empty-icon">
              <svg
                width="80"
                height="80"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 6.25278V19.2528M12 6.25278C10.8321 5.47686 9.24649 5 7.5 5C5.75351 5 4.16789 5.47686 3 6.25278V19.2528C4.16789 18.4769 5.75351 18 7.5 18C9.24649 18 10.8321 18.4769 12 19.2528M12 6.25278C13.1679 5.47686 14.7535 5 16.5 5C18.2465 5 19.8321 5.47686 21 6.25278V19.2528C19.8321 18.4769 18.2465 18 16.5 18C14.7535 18 13.1679 18.4769 12 19.2528"
                  stroke="#0d3b66"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <p class="empty-message">
              There are no maker activities available at the moment.
            </p>
          </div>

          <div v-else-if="!isLoading" class="lesson-grid">
            <ItemMakerCart
              v-for="(makerActivity, index) in makerActivities"
              :key="makerActivity.id"
              :item="makerActivity"
              :isLogged="isLogged"
              class="fade-in"
              :style="{ animationDelay: `${index * 0.05}s` }"
              @item-clicked="navigateToMakerCart(makerActivity)"
              @update-liked-status="updateMakerActivityLikedStatus"
            />
          </div>
        </div>
      </div>

      <!-- Mobile Carousel for Maker Activities -->
      <div v-else class="mobile-carousel-container">
        <swiper
          :modules="[SwiperPagination, SwiperNavigation, SwiperAutoplay]"
          :slides-per-view="1.2"
          :space-between="0"
          :centered-slides="true"
          :loop="makerActivities.length > 3"
          :autoplay="false"
          :grab-cursor="true"
          class="lesson-swiper"
        >
          <swiper-slide
            v-for="makerActivity in makerActivities"
            :key="makerActivity.id"
            class="lesson-slide"
          >
            <ItemMakerCart
              :item="makerActivity"
              :isLogged="isLogged"
              @item-clicked="navigateToMakerCart(makerActivity)"
              @update-liked-status="updateMakerActivityLikedStatus"
            />
          </swiper-slide>
        </swiper>
      </div>

      <div class="load-more-container" v-if="makerActivities.length > 0">
        <ActionButton
          variant="primary"
          @click="loadMoreMakerActivities"
          class="load-more-button"
          :disabled="isLoading"
        >
          <span>View More Maker Activities</span>
        </ActionButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import homepageService from "@/services/homepageService";
import stemStudioService from "@/services/stemStudioService";
import { computed, onMounted, ref, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import ActionButton from "../common/ActionButton.vue";
import LessonLibraryCard from "@/components/lesson-library/LessonLibraryCard.vue";
import ItemMakerCart from "@/components/maker-cart/ItemMakerCart.vue";

const store = useStore();
const isLogged = computed(() => !!store.state.user);

// Import Swiper Vue components
import { Swiper, SwiperSlide } from "swiper/vue";

// Import Swiper modules
import { Pagination, Navigation, Autoplay } from "swiper/modules";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

// Assign modules to variables Vue can use in the template
const SwiperPagination = Pagination;
const SwiperNavigation = Navigation;
const SwiperAutoplay = Autoplay;

const categories = [
  "Programming & Coding",
  "Design & Simulation",
  "App Development",
  "Artificial Intelligence",
  "Extended Reality (AR/VR/MR)",
  "Multimedia and Animation",
];

const makerFilter = ref({
  page: 1,
  limit: 6,
  sort_dir: "desc",
  sort_field: "Created",
  popular: true,
});

const lessonFilter = ref({
  page: 1,
  limit: 6,
  sort_dir: "desc",
  sort_by: "view",
  domain: [],
});

const selectedCategory = ref("");
const isLoading = ref(false);
const isLessonLoading = ref(false);
const isMobile = ref(false);

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

const selectCategory = (category) => {
  selectedCategory.value = selectedCategory.value === category ? "" : category;
  lessonFilter.value.page = 1;
  fetchLessons();
};

const clearFilters = () => {
  selectedCategory.value = "";
  lessonFilter.value.page = 1;
  fetchLessons();
};

const lessons = ref([]);

const makerActivities = ref([]);

const updateLessonLikedStatus = ({ id, liked }) => {
  const lesson = lessons.value.find((l) => l.id === id);
  if (lesson) lesson.isLiked = liked;
};

const updateMakerActivityLikedStatus = ({ id, liked }) => {
  const activity = makerActivities.value.find(
    (a) => a.makercart_id === id || a.id === id
  );
  if (activity) activity.liked = liked;
};

const fetchLessons = async () => {
  try {
    isLessonLoading.value = true;

    if (selectedCategory.value) {
      lessonFilter.value.domain = [selectedCategory.value];
    } else {
      lessonFilter.value.domain = [];
    }

    const response = await homepageService.getLessons(lessonFilter.value);

    // Small delay to make the loading state visible
    setTimeout(() => {
      lessons.value = response.data.data.rows;
      isLessonLoading.value = false;
    }, 600);
  } catch (error) {
    console.error("Error fetching lessons:", error);
    isLessonLoading.value = false;
  }
};

const fetchMakerActivities = async () => {
  try {
    isLoading.value = true;
    // Cache current activities to enable smooth transition
    // const currentActivities = [...makerActivities.value];

    const response = await stemStudioService.getAll(makerFilter.value);

    // Small delay to make the loading state visible
    setTimeout(() => {
      makerActivities.value = response.data.data.rows.map((item) => ({
        ...item,
        image:
          item.image_thumbnail && item.image_thumbnail.length > 0
            ? item.image_thumbnail[0]
            : "/images/placeholder-activity.jpg",
      }));
      isLoading.value = false;
    }, 600);
  } catch (error) {
    console.error("Error fetching maker activities:", error);
    isLoading.value = false;
  }
};

const navigateToMakerCart = (makerActivity) => {
  window.open(`/makercart-studio?item=${makerActivity.makercart_id}`, "_blank");
};

const viewLesson = (lesson) => {
  window.open(`/lesson-library/${lesson.slug}`, "_blank");
};

const isVideoPlaying = ref(false);

const playVideo = () => {
  isVideoPlaying.value = true;
};

const closeVideo = () => {
  isVideoPlaying.value = false;
};

const loadMoreLessons = () => {
  // Open new tab
  window.open("/lesson-library", "_blank");
};

const loadMoreMakerActivities = () => {
  // Open new tab
  window.open("/makercart-studio", "_blank");
};

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
  fetchLessons();
  fetchMakerActivities();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkMobile);
});
</script>

<style scoped>
.lesson-library {
  font-family:
    "League Spartan",
    -apple-system,
    sans-serif;
}

.header-background {
  border-radius: 16px;
  background: var(--white-hover-1);
  backdrop-filter: blur(4px);
  padding: 16px;
  margin-bottom: 20px;
}

.header-title {
  font-size: var(--fs-3xl);
  color: var(--primary-blue);
  text-align: center;
  margin: 0;
}

.lesson-library-description {
  text-align: center;
  margin: 0 auto 0;
  color: var(--primary-blue);
  font-style: normal;
  font-weight: 500;
  line-height: 32px;
  font-size: var(--fs-large);
}

.category-filters {
  width: 90%;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.8rem;
  margin-bottom: 2.5rem;
}

.category-button {
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 2rem;
  padding: 0.5rem 1.2rem;
  font-size: var(--fs-big);
  font-style: normal;
  font-weight: 700;
  line-height: 28px; /* 140% */
  letter-spacing: 0.03px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.category-button:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.category-button.active {
  background-color: #0d3b66;
  color: white;
  border-color: #0d3b66;
}

.lesson-cards {
  margin-bottom: 3rem;
  padding: 0 80px;
}

.lesson-grid {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.lesson-card {
  background-color: #f8f9fa;
  border-radius: 0.8rem;
  overflow: hidden;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
  cursor: pointer;
  animation: fadeIn 0.5s ease forwards;
  opacity: 0;
  transition:
    opacity 0.5s ease-out,
    transform 0.5s ease-out;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.lesson-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.card-image {
  height: 200px;
  background-size: cover;
  background-position: center;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-title {
  padding: 1.5rem 1rem;
  font-weight: 600;
  color: #0d3b66;
  margin: 0;
  font-size: 1.2rem;
}

.teacher-testimonials {
  padding: 2rem;
  border-radius: 0.8rem;
  margin-top: 2rem;
  margin-bottom: 3rem;
}

.video-container {
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  aspect-ratio: 16 / 9;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.video-container.video-playing {
  max-width: 900px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
}

.video-thumbnail {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  overflow: hidden;
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.video-thumbnail:hover .thumbnail-img {
  transform: scale(1.05);
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(0deg, rgba(13, 59, 102, 0.3), rgba(0, 0, 0, 0.1));
  z-index: 1;
}

.play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background-color: rgba(13, 59, 102, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: all 0.3s ease;
}

.play-button svg {
  width: 30px;
  height: 30px;
  margin-left: 5px;
}

.video-thumbnail:hover .play-button {
  background-color: #0d3b66;
  transform: translate(-50%, -50%) scale(1.1);
  box-shadow: 0 0 20px rgba(13, 59, 102, 0.4);
}

.video-title {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.eddy-logo-small {
  font-size: 1.8rem;
  font-weight: bold;
  color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.eddy-tagline {
  font-size: 1.2rem;
  font-weight: 500;
  color: white;
  margin: 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.youtube-video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.close-video-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 40px;
  height: 40px;
  background-color: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 5;
  transition: all 0.3s ease;
}

.close-video-btn:hover {
  background-color: rgba(0, 0, 0, 0.8);
  transform: scale(1.1);
}

/* Transitions and animations */
.fade-in {
  animation: fadeIn 0.5s ease forwards;
  opacity: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.lesson-card:nth-child(1) {
  animation-delay: 0.05s;
}

.lesson-card:nth-child(2) {
  animation-delay: 0.1s;
}

.lesson-card:nth-child(3) {
  animation-delay: 0.15s;
}

.lesson-card:nth-child(4) {
  animation-delay: 0.2s;
}

.lesson-card:nth-child(5) {
  animation-delay: 0.25s;
}

.lesson-card:nth-child(6) {
  animation-delay: 0.3s;
}

/* Empty state styles */
.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  text-align: center;
  min-height: 300px;
}

.empty-icon {
  margin-bottom: 1.5rem;
  opacity: 0.7;
}

.empty-title {
  font-size: 1.5rem;
  color: #0d3b66;
  margin: 0 0 0.5rem;
}

.empty-message {
  color: #666;
  margin: 0 0 1.5rem;
}

.reset-button {
  background-color: #0d3b66;
  color: white;
  border: none;
  border-radius: 2rem;
  padding: 0.6rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.reset-button:hover {
  background-color: #12507e;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 59, 102, 0.2);
}

.reset-button:active {
  transform: translateY(0);
}

.image-placeholder {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  text-align: center;
}

.placeholder-icon {
  margin-bottom: 0.75rem;
  color: #4c4ddc;
  opacity: 0.8;
}

.placeholder-text {
  font-size: 0.9rem;
  color: #0d3b66;
  font-weight: 600;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin: 0;
}

/* Pagination styles */
.maker-activities-container {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.cards-container {
  flex: 1;
  position: relative;
  min-height: 450px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
  backdrop-filter: blur(3px);
  border-radius: 12px;
}

.pagination-arrow {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  border: 1px solid #e0e0e0;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #0d3b66;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.left-arrow {
  margin-right: 15px;
}

.right-arrow {
  margin-left: 15px;
}

.pagination-arrow:hover:not(:disabled) {
  background-color: #f5f7fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.pagination-arrow:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-indicator-container {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
}

.page-indicator {
  font-size: 1rem;
  color: #0d3b66;
  font-weight: 500;
  min-width: 80px;
  text-align: center;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 5px 15px;
  border-radius: 20px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.pagination-loader {
  min-width: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loader-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(13, 59, 102, 0.2);
  border-top-color: #0d3b66;
  border-radius: 50%;
  animation: spinner 0.8s linear infinite;
}

.loader-spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(13, 59, 102, 0.2);
  border-top-color: #0d3b66;
  border-radius: 50%;
  animation: spinner 0.8s linear infinite;
}

@keyframes spinner {
  to {
    transform: rotate(360deg);
  }
}

/* Page transition animations */
.page-transition-move {
  transition: transform 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.page-transition-enter-active {
  transition: all 0.5s ease-out;
}

.page-transition-leave-active {
  transition: all 0.5s ease-in;
  position: absolute;
  width: calc((100% / 3) - 1.5rem);
}

.slide-right .page-transition-enter-from,
.slide-left .page-transition-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.slide-left .page-transition-enter-from,
.slide-right .page-transition-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Staggered enter transition */
.lesson-card {
  transition:
    opacity 0.5s ease-out,
    transform 0.5s ease-out;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .header-title {
    font-size: 1.8rem;
  }

  .lesson-library-description {
    font-size: 1.1rem;
    margin: 0 auto 1.5rem;
  }

  .lesson-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  .page-transition-leave-active {
    width: calc((100% / 2) - 1rem);
  }

  .category-filters {
    width: 100%;
    gap: 0.6rem;
  }

  .category-button {
    font-size: 1.1rem;
    padding: 0.5rem 1rem;
  }
}

@media (max-width: 768px) {
  .header-title {
    font-size: 1.6rem;
  }

  .lesson-library-description {
    font-size: 1rem;
    line-height: 1.5;
    margin: 0 auto 1.2rem;
    padding: 0 1rem;
  }

  .lesson-grid {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }

  .page-transition-leave-active {
    width: calc(100% - 1rem);
  }

  .category-filters {
    overflow-x: auto;
    padding: 0.5rem;
    flex-wrap: nowrap;
    justify-content: flex-start;
    margin-bottom: 1.5rem;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    /* Firefox */
    -ms-overflow-style: none;
    /* IE and Edge */
  }

  .category-filters::-webkit-scrollbar {
    display: none;
    /* Chrome, Safari, Opera */
  }

  .lesson-cards {
    padding: 0;
  }

  .category-button {
    font-size: 0.9rem;
    padding: 0.4rem 0.8rem;
    flex-shrink: 0;
  }

  .card-title {
    padding: 1rem 0.8rem;
    font-size: 1rem;
  }

  .maker-activities-container {
    /* flex-direction: column; */
    margin-bottom: 0.5rem;
  }

  .pagination-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 36px;
    height: 36px;
    z-index: 20;
  }

  .left-arrow {
    left: -5px;
    margin-right: 0;
  }

  .right-arrow {
    right: -5px;
    margin-left: 0;
  }

  .empty-state {
    padding: 2rem 1rem;
  }

  .empty-title {
    font-size: 1.3rem;
  }

  .empty-message {
    font-size: 0.9rem;
  }

  .play-button {
    width: 60px;
    height: 60px;
  }

  .play-button svg {
    width: 24px;
    height: 24px;
  }

  .eddy-logo-small {
    font-size: 1.4rem;
  }

  .eddy-tagline {
    font-size: 1rem;
  }

  .teacher-testimonials {
    padding: 1rem;
  }

  .load-more-button {
    padding: 0.7rem 1.5rem;
    font-size: 0.9rem;
    border-radius: 12px;
  }
}

@media (max-width: 480px) {
  .header-title {
    font-size: var(--font-size-headline-small);
  }

  .teacher-testimonials {
    padding: 0;
  }

  .lesson-library-description {
    font-size: var(--font-size-title-medium);
    margin: 0 auto 1rem;
  }

  .category-button {
    font-size: var(--fs-big);
    padding: 0.3rem 0.7rem;
    font-weight: 700;
  }

  .card-title {
    padding: 0.8rem 0.6rem;
    font-size: 0.9rem;
  }

  .pagination-arrow {
    width: 32px;
    height: 32px;
  }

  .play-button {
    width: 50px;
    height: 50px;
  }

  .play-button svg {
    width: 20px;
    height: 20px;
  }

  .eddy-logo-small {
    font-size: 1.2rem;
  }

  .eddy-tagline {
    font-size: 0.9rem;
  }

  .load-more-button {
    padding: 0.6rem 1.2rem;
    font-size: 0.85rem;
    width: 100%;
  }

  .cards-container {
    min-height: 300px;
  }
}

.load-more-container {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}

.load-more-button {
  min-width: 150px;
}

.load-more-button:hover:not(:disabled) {
  background-color: #12507e;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 59, 102, 0.2);
}

.load-more-button:active:not(:disabled) {
  transform: translateY(0);
}

.load-more-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Mobile carousel styles */
.mobile-carousel-container {
  width: 100%;
  overflow: hidden;
}

.lesson-swiper {
  overflow: visible !important;
  padding: 0 10px;
}

.lesson-slide {
  opacity: 0.4;
  transform: scale(0.85);
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
  display: flex;
  justify-content: center;
}

.lesson-swiper .swiper-slide-active {
  opacity: 1;
  transform: scale(1);
}

.lesson-swiper .lesson-card,
.lesson-swiper .item-maker-cart {
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  opacity: 1 !important; /* Override fade-in animation for active slide */
  animation: none !important; /* Disable fade-in for swiper slides to avoid conflict */
}
</style>
